import { useState, useEffect, useCallback } from "react";

// ─── Tab Partners — khusus CrunchAlpha admin (induk) untuk kelola white-label ──
// File BERDIRI SENDIRI (tidak import dari AdminDashboard.jsx -> hindari circular
// import). Semua warna pakai CSS variable, 0 hardcode hex.
// Endpoint (semua /api/admin/*, adminRoutes):
//   GET/POST /partners, PUT /partners/:id
//   GET/POST /partners/:id/fee-rules
//   GET      /partners/:id/revenue-report?month=YYYY-MM
//   GET      /partners/:id/users, PUT /partners/:id/users/:userId/partner-admin

const C = {
  card: "var(--bg-surface)", elevated: "var(--bg-elevated)", bg: "var(--bg)",
  border: "var(--border)", text: "var(--text-main)", muted: "var(--text-muted)",
  accent: "var(--accent)", success: "var(--success)", danger: "var(--danger)",
  warn: "var(--warning)",
};

const getToken = () => localStorage.getItem("access_token") || localStorage.getItem("token") || "";

const call = async (url, method = "GET", body = null) => {
  const r = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  let j = null;
  try { j = await r.json(); } catch (e) { /* body kosong / bukan JSON */ }
  if (!r.ok) throw new Error((j && (j.error || j.message)) || `HTTP ${r.status}`);
  return j;
};

const fmtUSD = (n) => (n == null || isNaN(Number(n)) ? "-" : "$" + Number(n).toFixed(2));
const PARTNER_TYPES = ["fund_manager", "prop_firm", "broker"];

// Skema field per fee_type — HARUS sinkron dengan FEE_PARAM_SCHEMA di
// AdminDashboard.jsx (key sama dengan yang dibaca backend dari fee_rules.params).
const FEE_SCHEMA = {
  performance: [
    { key: "trader_pct", label: "Trader Fee %" },
    { key: "analyst_pct", label: "Analyst Fee %" },
    { key: "trader_split_pct", label: "Split ke Trader %" },
  ],
  volume: [
    { key: "per_lot_usd", label: "Fee per Lot (USD)" },
    { key: "trader_split_pct", label: "Split ke Trader %" },
  ],
  management: [
    { key: "annual_rate_pct", label: "Rate Tahunan %" },
    { key: "trader_split_pct", label: "Split ke Trader %" },
  ],
  platform: [{ key: "flat_usd_per_month", label: "Flat Fee per Bulan (USD)" }],
  joining: [{ key: "flat_usd", label: "Flat Fee Sekali Bayar (USD)" }],
  subscription: [{ key: "flat_usd", label: "Flat Fee per Bulan (USD)" }],
};

const summarize = (feeId, params) => {
  const schema = FEE_SCHEMA[feeId] || [];
  const p = params || {};
  const parts = schema.filter((f) => p[f.key] !== undefined && p[f.key] !== "").map((f) => `${f.label}: ${p[f.key]}`);
  return parts.length ? parts.join(" · ") : "Belum diatur";
};

// ─── UI primitives lokal ──────────────────────────────────────────────────────
const inputStyle = {
  width: "100%", padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: 6,
  fontSize: 13, background: C.elevated, color: C.text, boxSizing: "border-box",
};

const Btn = ({ onClick, children, danger, primary, small, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      padding: small ? "4px 10px" : "8px 16px", fontSize: small ? 11 : 13, fontWeight: 700, borderRadius: 6,
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
      border: `1px solid ${danger ? C.danger : primary ? C.accent : C.border}`,
      background: primary ? C.accent : "transparent",
      color: danger ? C.danger : primary ? "#fff" : C.text,
    }}
  >
    {children}
  </button>
);

const Field = ({ label, hint, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4, fontWeight: 600, textTransform: "uppercase" }}>{label}</label>
    {children}
    {hint && <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{hint}</div>}
  </div>
);

// Modal HANYA bisa ditutup lewat tombol eksplisit (pelajaran 2026-09-08: klik
// overlay tidak boleh menutup modal dan menghapus input yang sudah diisi).
const Modal = ({ title, onClose, wide, children }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, width: "100%", maxWidth: wide ? 760 : 520, maxHeight: "90vh", overflow: "auto", padding: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{title}</div>
        <Btn small onClick={onClose}>Tutup</Btn>
      </div>
      {children}
    </div>
  </div>
);

const Pill = ({ on, onText, offText }) => (
  <span style={{
    padding: "2px 10px", fontSize: 11, fontWeight: 700, borderRadius: 20,
    border: `1px solid ${on ? C.success : C.border}`, color: on ? C.success : C.muted,
  }}>{on ? onText : offText}</span>
);

// ─── Modal: Create / Edit partner ─────────────────────────────────────────────
function EditModal({ partner, onClose, onSaved }) {
  const isEdit = !!partner;
  const origCfg = JSON.stringify(partner?.displayConfig || {}, null, 2);
  const [f, setF] = useState({
    slug: partner?.slug || "",
    displayName: partner?.displayName || "",
    partnerType: partner?.partnerType || "fund_manager",
    domain: partner?.domain || "",
    logoUrl: partner?.logoUrl || "",
    faviconUrl: partner?.faviconUrl || "",
    primaryColor: partner?.primaryColor || "",
    secondaryColor: partner?.secondaryColor || "",
    footerText: partner?.footerText || "",
    revenueSharePct: partner?.revenueSharePct ?? "",
    minMonthlyFeeUsd: partner?.minMonthlyFeeUsd ?? "",
    displayConfig: origCfg,
    providerMode: partner?.signupPolicy?.providerMode || "",
    plan: partner?.plan || "",
    maxProviders: partner?.maxProviders ?? "",
    maxInvestors: partner?.maxInvestors ?? "",
  });
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setMsg("");
    const name = f.displayName.trim();
    if (!name) return setMsg("Display name wajib diisi");
    if (!isEdit && !/^[a-z0-9][a-z0-9-]{1,62}$/.test(f.slug.trim())) {
      return setMsg("Slug: huruf kecil, angka, tanda minus; minimal 2 karakter");
    }
    const domain = f.domain.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    for (const k of ["primaryColor", "secondaryColor"]) {
      const v = f[k].trim();
      if (v && !/^#[0-9a-fA-F]{6}$/.test(v)) return setMsg("Warna harus format #RRGGBB (6 digit hex)");
    }
    const rs = f.revenueSharePct === "" ? undefined : Number(f.revenueSharePct);
    if (rs !== undefined && (isNaN(rs) || rs < 0 || rs > 100)) return setMsg("Revenue share harus 0 sampai 100");
    const mf = f.minMonthlyFeeUsd === "" ? undefined : Number(f.minMonthlyFeeUsd);
    if (mf !== undefined && (isNaN(mf) || mf < 0)) return setMsg("Minimum fee harus angka 0 atau lebih");

    let cfg;
    if (f.displayConfig.trim() !== origCfg.trim()) {
      try {
        cfg = JSON.parse(f.displayConfig);
        if (typeof cfg !== "object" || cfg === null || Array.isArray(cfg)) throw new Error("bukan object");
      } catch (e) {
        return setMsg("Display config harus JSON object yang valid");
      }
    }

    // Field kosong sengaja TIDAK dikirim: backend pakai COALESCE, jadi nilai
    // lama dipertahankan (batasan: field belum bisa dikosongkan lewat UI ini).
    const nz = (v) => (v.trim() === "" ? undefined : v.trim());
    const body = {
      displayName: name,
      domain: domain || undefined,
      logoUrl: nz(f.logoUrl), faviconUrl: nz(f.faviconUrl),
      primaryColor: nz(f.primaryColor), secondaryColor: nz(f.secondaryColor),
      footerText: nz(f.footerText),
      revenueSharePct: rs, minMonthlyFeeUsd: mf,
      providerMode: f.providerMode,
      plan: f.plan,
      maxProviders: f.maxProviders === "" ? -1 : Number(f.maxProviders),
      maxInvestors: f.maxInvestors === "" ? -1 : Number(f.maxInvestors),
      displayConfig: cfg,
    };
    setSaving(true);
    try {
      if (isEdit) {
        await call(`/api/admin/partners/${partner.id}`, "PUT", body);
      } else {
        await call("/api/admin/partners", "POST", { ...body, slug: f.slug.trim(), partnerType: f.partnerType, displayConfig: cfg || {} });
      }
      onSaved();
    } catch (e) {
      setMsg(e.message);
    }
    setSaving(false);
  };

  return (
    <Modal title={isEdit ? `Edit partner — ${partner.displayName}` : "Tambah partner"} onClose={onClose}>
      {!isEdit && (
        <Field label="Slug" hint="Pengenal unik, tidak bisa diubah setelah dibuat. Contoh: acme-capital">
          <input style={inputStyle} value={f.slug} onChange={set("slug")} placeholder="acme-capital" />
        </Field>
      )}
      <Field label="Display name">
        <input style={inputStyle} value={f.displayName} onChange={set("displayName")} placeholder="Acme Capital" />
      </Field>
      {!isEdit && (
        <Field label="Tipe partner">
          <select style={inputStyle} value={f.partnerType} onChange={set("partnerType")}>
            {PARTNER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      )}
      <Field label="Pendaftaran trader/analis" hint="Siapa yang boleh mendaftar sebagai trader atau analis di komunitas ini. Investor selalu boleh mendaftar. Partner tetap bisa mengubahnya sendiri dari halaman Partner Admin.">
        <select style={inputStyle} value={f.providerMode} onChange={set("providerMode")}>
          <option value="">Ikuti tipe partner (fund manager dan prop firm: ditunjuk, broker: terbuka)</option>
          <option value="open">Terbuka - siapa saja boleh daftar</option>
          <option value="appointed">Hanya ditunjuk partner</option>
        </select>
      </Field>
      <Field label="Paket" hint="Batas akun komunitas. Starter: 20 trader/analis + 100 investor. Growth: 50 + 300. Angka bisa diubah, dan kosong berarti tanpa batas. Akun yang sudah ada tidak dihapus kalau batas terlewati.">
        <select style={inputStyle} value={f.plan} onChange={(e) => { const v = e.target.value; const P = { starter: [20, 100], growth: [50, 300] }; setF({ ...f, plan: v, ...(P[v] ? { maxProviders: P[v][0], maxInvestors: P[v][1] } : {}) }); }}>
          <option value="">Tanpa paket (tanpa batas)</option>
          <option value="starter">Starter - 20 trader/analis + 100 investor</option>
          <option value="growth">Growth - 50 trader/analis + 300 investor</option>
          <option value="custom">Custom - isi batas manual</option>
        </select>
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Batas trader/analis" hint="Kosong = tanpa batas">
          <input style={inputStyle} type="number" min="0" value={f.maxProviders} onChange={set("maxProviders")} />
        </Field>
        <Field label="Batas investor" hint="Kosong = tanpa batas">
          <input style={inputStyle} type="number" min="0" value={f.maxInvestors} onChange={set("maxInvestors")} />
        </Field>
      </div>
      <Field label="Domain" hint="Subdomain *.crunchalpha.com langsung jalan (wildcard DNS + SSL sudah ada). Domain milik partner sendiri butuh setup nginx + SSL manual.">
        <input style={inputStyle} value={f.domain} onChange={set("domain")} placeholder="acme.crunchalpha.com" />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Warna utama" hint="#RRGGBB">
          <input style={inputStyle} value={f.primaryColor} onChange={set("primaryColor")} placeholder="#RRGGBB" />
        </Field>
        <Field label="Warna kedua" hint="#RRGGBB">
          <input style={inputStyle} value={f.secondaryColor} onChange={set("secondaryColor")} placeholder="#RRGGBB" />
        </Field>
      </div>
      <Field label="Logo URL"><input style={inputStyle} value={f.logoUrl} onChange={set("logoUrl")} placeholder="https://..." /></Field>
      <Field label="Favicon URL"><input style={inputStyle} value={f.faviconUrl} onChange={set("faviconUrl")} placeholder="https://..." /></Field>
      <Field label="Footer text"><input style={inputStyle} value={f.footerText} onChange={set("footerText")} /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Revenue share %" hint="Bagian CrunchAlpha dari platform share partner">
          <input style={inputStyle} type="number" step="any" value={f.revenueSharePct} onChange={set("revenueSharePct")} />
        </Field>
        <Field label="Minimum fee / bulan (USD)" hint="Lantai tagihan bulanan">
          <input style={inputStyle} type="number" step="any" value={f.minMonthlyFeeUsd} onChange={set("minMonthlyFeeUsd")} />
        </Field>
      </div>
      <Field label="Display config (lanjutan, JSON)" hint="Visibility/access/branding/terminology. Hanya dikirim kalau Anda mengubahnya.">
        <textarea style={{ ...inputStyle, fontFamily: "monospace", minHeight: 90 }} value={f.displayConfig} onChange={set("displayConfig")} />
      </Field>
      {msg && <div style={{ fontSize: 12, color: C.danger, marginBottom: 10 }}>{msg}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn onClick={onClose}>Batal</Btn>
        <Btn primary onClick={save} disabled={saving}>{saving ? "Menyimpan..." : "Simpan"}</Btn>
      </div>
    </Modal>
  );
}

// ─── Modal: Fee override per partner ──────────────────────────────────────────
function FeesModal({ partner, onClose }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [edit, setEdit] = useState(null); // { id, is_enabled, params }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const j = await call(`/api/admin/partners/${partner.id}/fee-rules`);
      setRules(j.data || []);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [partner.id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const schema = FEE_SCHEMA[edit.id] || [];
    const params = {};
    for (const f of schema) {
      const v = edit.params?.[f.key];
      const n = v === "" || v === undefined ? NaN : Number(v);
      if (isNaN(n)) {
        if (edit.is_enabled) return setErr(`Isi semua field untuk mengaktifkan (${f.label})`);
      } else {
        params[f.key] = n;
      }
    }
    setSaving(true); setErr("");
    try {
      await call(`/api/admin/partners/${partner.id}/fee-rules`, "POST", { fee_type_id: edit.id, is_enabled: edit.is_enabled, params });
      setEdit(null);
      await load();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  return (
    <Modal title={`Fee override — ${partner.displayName}`} onClose={onClose} wide>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
        Override partner menggantikan SELURUH aturan global untuk fee tersebut, jadi semua field harus diisi lengkap.
        Fee tanpa override mengikuti aturan global CrunchAlpha.
      </div>
      {err && <div style={{ fontSize: 12, color: C.danger, marginBottom: 10 }}>{err}</div>}
      {loading ? <div style={{ color: C.muted, fontSize: 13 }}>Memuat...</div> : (
        <div style={{ display: "grid", gap: 10 }}>
          {rules.map((r) => {
            const open = edit && edit.id === r.fee_type_id;
            return (
              <div key={r.fee_type_id} style={{ background: C.elevated, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{r.label}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{r.trigger_model}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {r.has_override
                      ? <Pill on={r.is_enabled} onText="Override ON" offText="Override OFF" />
                      : <span style={{ fontSize: 11, color: C.muted }}>Ikut global</span>}
                    {!open && (
                      <Btn small onClick={() => { setErr(""); setEdit({ id: r.fee_type_id, is_enabled: r.has_override ? r.is_enabled : true, params: r.params || {} }); }}>Atur</Btn>
                    )}
                  </div>
                </div>
                {r.has_override && !open && (
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>{summarize(r.fee_type_id, r.params)}</div>
                )}
                {open && (
                  <div style={{ marginTop: 12 }}>
                    <label style={{ fontSize: 12, color: C.text, display: "flex", gap: 6, alignItems: "center", marginBottom: 10 }}>
                      <input type="checkbox" checked={edit.is_enabled} onChange={(e) => setEdit({ ...edit, is_enabled: e.target.checked })} />
                      Aktif untuk partner ini
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
                      {(FEE_SCHEMA[edit.id] || []).map((fld) => (
                        <Field key={fld.key} label={fld.label}>
                          <input style={inputStyle} type="number" step="any" value={edit.params?.[fld.key] ?? ""}
                            onChange={(e) => setEdit({ ...edit, params: { ...edit.params, [fld.key]: e.target.value } })} />
                        </Field>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      <Btn small onClick={() => setEdit(null)}>Batal</Btn>
                      <Btn small primary onClick={save} disabled={saving}>{saving ? "Menyimpan..." : "Simpan"}</Btn>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ─── Modal: Revenue report bulanan ────────────────────────────────────────────
function RevenueModal({ partner, onClose }) {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr("");
      try {
        const j = await call(`/api/admin/partners/${partner.id}/revenue-report?month=${month}`);
        if (alive) setRep(j.data || j);
      } catch (e) { if (alive) { setErr(e.message); setRep(null); } }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [partner.id, month]);

  const card = (label, value, color) => (
    <div style={{ background: C.elevated, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
      <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: color || C.text, marginTop: 4 }}>{value}</div>
    </div>
  );

  return (
    <Modal title={`Revenue report — ${partner.displayName}`} onClose={onClose} wide>
      <Field label="Bulan">
        <input style={{ ...inputStyle, maxWidth: 200 }} type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
      </Field>
      {partner.revenueSharePct == null && (
        <div style={{ fontSize: 12, color: C.warn, marginBottom: 10 }}>
          Revenue share partner ini belum diisi — hasil report akan 0. Atur lewat tombol Edit.
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: C.danger, marginBottom: 10 }}>{err}</div>}
      {loading ? <div style={{ color: C.muted, fontSize: 13 }}>Memuat...</div> : rep && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, marginBottom: 14 }}>
            {card("Total platform share", fmtUSD(rep.total_partner_platform_share))}
            {card(`Revenue share (${rep.revenue_share_pct ?? 0}%)`, fmtUSD(rep.revenue_share_amount))}
            {card("Minimum fee", rep.min_monthly_fee_usd == null ? "-" : fmtUSD(rep.min_monthly_fee_usd))}
            {card(rep.floor_applied ? "Tagihan (lantai berlaku)" : "Tagihan ke partner", fmtUSD(rep.final_amount), C.accent)}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ color: C.muted, textAlign: "left" }}>
                <th style={{ padding: "6px 8px" }}>Fee</th>
                <th style={{ padding: "6px 8px" }}>Platform share</th>
                <th style={{ padding: "6px 8px" }}>Bagian CrunchAlpha</th>
              </tr>
            </thead>
            <tbody>
              {(rep.breakdown || []).map((b) => (
                <tr key={b.fee_type} style={{ borderTop: `1px solid ${C.border}`, color: C.text }}>
                  <td style={{ padding: "6px 8px" }}>{b.label || b.fee_type}</td>
                  <td style={{ padding: "6px 8px" }}>{fmtUSD(b.partner_platform_share)}</td>
                  <td style={{ padding: "6px 8px" }}>{fmtUSD(b.crunchalpha_cut)}</td>
                </tr>
              ))}
              {(rep.breakdown || []).length === 0 && (
                <tr><td colSpan={3} style={{ padding: "10px 8px", color: C.muted }}>Belum ada fee tercatat di bulan ini.</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </Modal>
  );
}

// ─── Modal: Partner Admin (siapa yang boleh mengelola partner ini) ────────────
function AdminsModal({ partner, onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const j = await call(`/api/admin/partners/${partner.id}/users`);
      setUsers(j.data || []);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [partner.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (u) => {
    const next = !u.isPartnerAdmin;
    const adminCount = users.filter((x) => x.isPartnerAdmin).length;
    let msg = next
      ? `Jadikan ${u.email} sebagai Partner Admin "${partner.displayName}"?\n\nIa akan bisa mengelola marketplace, fee, dan user partner ini.`
      : `Cabut akses Partner Admin dari ${u.email}?`;
    if (!next && adminCount <= 1) {
      msg += "\n\nPERHATIAN: ini admin terakhir. Partner tidak punya admin sampai Anda menunjuk yang baru.";
    }
    if (next && u.status !== "active") {
      msg += `\n\nCatatan: status akun ini "${u.status}".`;
    }
    if (!window.confirm(msg)) return;
    setBusyId(u.id); setErr("");
    try {
      await call(`/api/admin/partners/${partner.id}/users/${u.id}/partner-admin`, "PUT", { isPartnerAdmin: next });
      await load();
    } catch (e) { setErr(e.message); }
    setBusyId("");
  };

  const needle = q.trim().toLowerCase();
  const shown = users.filter((u) => !needle || u.email.toLowerCase().includes(needle) || (u.name || "").toLowerCase().includes(needle));
  const adminCount = users.filter((x) => x.isPartnerAdmin).length;
  const th = { padding: "6px 8px", fontSize: 11, color: C.muted, textAlign: "left", textTransform: "uppercase", fontWeight: 600 };
  const td = { padding: "8px", fontSize: 13, color: C.text, borderTop: `1px solid ${C.border}`, verticalAlign: "middle" };

  return (
    <Modal title={`Partner Admin — ${partner.displayName}`} onClose={onClose} wide>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
        Hanya user yang mendaftar lewat domain partner ini yang bisa dijadikan Partner Admin. Akun dengan email sama di
        partner lain adalah akun terpisah dan tidak muncul di sini. Admin saat ini: {adminCount}.
      </div>
      <Field label="Cari user">
        <input style={inputStyle} value={q} onChange={(e) => setQ(e.target.value)} placeholder="email atau nama" />
      </Field>
      {err && <div style={{ fontSize: 12, color: C.danger, marginBottom: 10 }}>{err}</div>}
      {adminCount === 0 && !loading && (
        <div style={{ fontSize: 12, color: C.warn, marginBottom: 10 }}>
          Partner ini belum punya admin. Tunjuk satu user agar partner bisa mengelola marketplace dan fee-nya sendiri.
        </div>
      )}
      {loading ? <div style={{ color: C.muted, fontSize: 13 }}>Memuat...</div> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr><th style={th}>User</th><th style={th}>Peran</th><th style={th}>Status</th><th style={th}>Admin</th><th style={th}>Aksi</th></tr>
            </thead>
            <tbody>
              {shown.map((u) => (
                <tr key={u.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 700 }}>{u.email}</div>
                    {u.name && <div style={{ fontSize: 11, color: C.muted }}>{u.name}</div>}
                  </td>
                  <td style={td}>{u.primaryRole}</td>
                  <td style={td}>{u.status}</td>
                  <td style={td}><Pill on={u.isPartnerAdmin} onText="Admin" offText="-" /></td>
                  <td style={td}>
                    <Btn small danger={u.isPartnerAdmin} disabled={busyId === u.id} onClick={() => toggle(u)}>
                      {busyId === u.id ? "..." : u.isPartnerAdmin ? "Cabut" : "Jadikan Admin"}
                    </Btn>
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr><td colSpan={5} style={{ ...td, color: C.muted, textAlign: "center" }}>
                  {users.length === 0 ? "Belum ada user di partner ini. User harus mendaftar lewat domain partner dulu." : "Tidak ada yang cocok."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// ─── Tab utama ────────────────────────────────────────────────────────────────
export default function TabPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null); // { kind: 'edit'|'fees'|'revenue', partner }

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const j = await call("/api/admin/partners");
      setPartners(j.data || []);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (p) => {
    const next = !p.isActive;
    const warn = next
      ? `Aktifkan partner "${p.displayName}"?`
      : `Nonaktifkan "${p.displayName}"? Domain ${p.domain || "partner"} akan diperlakukan seperti domain biasa sampai diaktifkan lagi.`;
    if (!window.confirm(warn)) return;
    try {
      await call(`/api/admin/partners/${p.id}`, "PUT", { isActive: next });
      load();
    } catch (e) { setErr(e.message); }
  };

  const th = { padding: "8px 10px", fontSize: 11, color: C.muted, textAlign: "left", textTransform: "uppercase", fontWeight: 600 };
  const td = { padding: "10px", fontSize: 13, color: C.text, borderTop: `1px solid ${C.border}`, verticalAlign: "middle" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>White-label partners ({partners.length})</div>
          <div style={{ fontSize: 12, color: C.muted }}>Kelola partner, fee override, dan tagihan revenue share.</div>
        </div>
        <Btn primary onClick={() => setModal({ kind: "edit", partner: null })}>+ Tambah partner</Btn>
      </div>

      {err && <div style={{ fontSize: 12, color: C.danger, marginBottom: 10 }}>{err}</div>}
      {loading ? <div style={{ color: C.muted, fontSize: 13, padding: "30px 0", textAlign: "center" }}>Memuat...</div> : (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Partner</th><th style={th}>Domain</th><th style={th}>Tipe</th>
                <th style={th}>Status</th><th style={th}>Marketplace</th>
                <th style={th}>Rev. share</th><th style={th}>Min. fee</th><th style={th}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr key={p.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                      {p.primaryColor && <span title={p.primaryColor} style={{ width: 10, height: 10, borderRadius: 3, background: p.primaryColor, display: "inline-block" }} />}
                      {p.displayName}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted }}>{p.slug}</div>
                  </td>
                  <td style={td}>{p.domain || <span style={{ color: C.muted }}>-</span>}</td>
                  <td style={td}>{p.partnerType}</td>
                  <td style={td}>
                    <button onClick={() => toggleActive(p)} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0 }}>
                      <Pill on={p.isActive} onText="Aktif" offText="Nonaktif" />
                    </button>
                  </td>
                  <td style={td}>{p.marketplaceMode || <span style={{ color: C.muted }}>-</span>}</td>
                  <td style={td}>{p.revenueSharePct == null ? <span style={{ color: C.warn }}>belum diatur</span> : `${p.revenueSharePct}%`}</td>
                  <td style={td}>{p.minMonthlyFeeUsd == null ? "-" : fmtUSD(p.minMonthlyFeeUsd)}</td>
                  <td style={td}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Btn small onClick={() => setModal({ kind: "edit", partner: p })}>Edit</Btn>
                      <Btn small onClick={() => setModal({ kind: "fees", partner: p })}>Fee</Btn>
                      <Btn small onClick={() => setModal({ kind: "revenue", partner: p })}>Revenue</Btn>
                      <Btn small onClick={() => setModal({ kind: "admins", partner: p })}>Admin</Btn>
                    </div>
                  </td>
                </tr>
              ))}
              {partners.length === 0 && (
                <tr><td colSpan={8} style={{ ...td, color: C.muted, textAlign: "center" }}>Belum ada partner.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal?.kind === "edit" && (
        <EditModal partner={modal.partner} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal?.kind === "fees" && <FeesModal partner={modal.partner} onClose={() => setModal(null)} />}
      {modal?.kind === "revenue" && <RevenueModal partner={modal.partner} onClose={() => setModal(null)} />}
      {modal?.kind === "admins" && <AdminsModal partner={modal.partner} onClose={() => setModal(null)} />}
    </div>
  );
}